import { useEffect, useState } from "react";
import { Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { Empty, Field, Badge } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { addDays, fmtDate, todayISO, weekStart } from "../../lib/format";
import { cn } from "../../lib/utils";
import { NO_FILTERS, SITE_FILTERS, SiteFilter } from "./components";

const TONE = { ok: "bg-emerald-950 border-emerald-600 text-emerald-200", conflitto: "bg-red-950 border-red-600 text-red-200", gia_presente: "bg-slate-800 border-slate-600 text-slate-300" };
const LABEL = { ok: "✓ Copiabile", conflitto: "⚠ Conflitto", gia_presente: "Già presente" };

function PreviewItem({ it }) {
  return (
    <li className="py-2.5 text-sm" data-testid={`copy-week-item-${it.source_id}`}>
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <span className="font-medium">{it.resource_type === "worker" ? "👷" : "🚚"} {it.name} <span className="text-muted-foreground">· {it.site_name}</span></span>
        <span className="flex items-center gap-2"><span className="num text-xs">{fmtDate(it.start_date)} → {fmtDate(it.end_date)}</span><Badge className={TONE[it.status]}>{LABEL[it.status]}</Badge></span>
      </div>
      {it.issues.map((m) => <div key={m} className={cn("text-xs mt-1", it.status === "conflitto" ? "text-red-300" : "text-muted-foreground")}>{m}</div>)}
    </li>
  );
}

function CopyPreview({ preview, busy, onCancel, onConfirm }) {
  return (
    <>
      <div className="flex flex-wrap gap-2 text-sm" data-testid="copy-week-summary">
        <Badge className={TONE.ok}>{preview.ok_count} copiabili</Badge>
        <Badge className={TONE.conflitto}>{preview.issue_count} con problemi</Badge>
        <Badge className={TONE.gia_presente}>{preview.skipped_count} già pianificati</Badge>
      </div>
      {!preview.items.length ? <Empty title="Nessuna assegnazione nella settimana di origine" /> : (
        <ul className="divide-y divide-border/60 max-h-[40vh] overflow-y-auto scrollbar-thin">
          {preview.items.map((it) => <PreviewItem key={`${it.resource_type}-${it.source_id}`} it={it} />)}
        </ul>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>Annulla</Button>
        <Button onClick={onConfirm} disabled={busy || !preview.ok_count} data-testid="copy-week-confirm-btn">Conferma copia ({preview.ok_count})</Button>
      </div>
    </>
  );
}

function useCopyWeek({ open, onOpenChange, siteId, onDone }) {
  const [src, setSrc] = useState(weekStart(todayISO()));
  const [onlySite, setOnlySite] = useState(true);
  const [filters, setFilters] = useState(NO_FILTERS);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const single = Boolean(onlySite && siteId);
  const finish = (data) => {
    toast.success(`✓ Pianificazione copiata: ${data.created} assegnazioni create`);
    if (data.issue_count) toast.warning(`⚠ ${data.issue_count} elementi non copiati per conflitti`);
    onDone();
    onOpenChange(false);
  };
  const run = async (dry) => {
    setBusy(true);
    try {
      const scope = single ? { site_id: Number(siteId) } : { site_type: filters.site_type || null, site_status: filters.status || null };
      const r = await api.post("/planning/copy-week", { source_week_start: src, ...scope, dry_run: dry });
      setPreview(r.data);
      if (!dry) finish(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { if (open) setPreview(null); }, [open, src, onlySite, filters]);
  return { src, setSrc, onlySite, setOnlySite, filters, setFilters, preview, busy, single, run };
}

function CopyScope({ c, siteId, sites }) {
  return (
    <>
      <div className="grid sm:grid-cols-[1fr_auto_auto] gap-3 items-end">
        <Field label="Settimana di origine (qualsiasi giorno)"><Input type="date" value={c.src} onChange={(e) => c.setSrc(weekStart(e.target.value))} data-testid="copy-week-source-input" /></Field>
        {siteId && <label className="flex items-center gap-2 text-sm h-10"><Switch checked={c.onlySite} onCheckedChange={c.setOnlySite} />Solo cantiere selezionato</label>}
        <Button onClick={() => c.run(true)} disabled={c.busy} data-testid="copy-week-analyze-btn">{c.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Analizza"}</Button>
      </div>
      {!c.single && (
        <div className="grid grid-cols-2 gap-3" data-testid="copy-week-filters">
          {SITE_FILTERS.map((cfg) => (
            <SiteFilter key={cfg.key} cfg={cfg} sites={sites} value={c.filters[cfg.key]} testidPrefix="copy-"
              onChange={(k, v) => c.setFilters((p) => ({ ...p, [k]: v }))} />
          ))}
        </div>
      )}
      <div className="text-xs text-muted-foreground num">Origine {fmtDate(c.src)} → {fmtDate(addDays(c.src, 6))} · Destinazione {fmtDate(addDays(c.src, 7))} → {fmtDate(addDays(c.src, 13))}</div>
    </>
  );
}

export default function CopyWeekDialog({ open, onOpenChange, siteId, sites, onDone }) {
  const c = useCopyWeek({ open, onOpenChange, siteId, onDone });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-popover max-w-3xl max-h-[90vh] overflow-y-auto scrollbar-thin" data-testid="copy-week-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Copy className="h-5 w-5 text-amber-400" />COPIA PIANIFICAZIONE</DialogTitle>
          <DialogDescription>Copia le assegnazioni di una settimana nella settimana successiva. Prima del salvataggio vengono verificati conflitti, assenze e disponibilità mezzi.</DialogDescription>
        </DialogHeader>
        <CopyScope c={c} siteId={siteId} sites={sites} />
        {c.preview && <CopyPreview preview={c.preview} busy={c.busy} onCancel={() => onOpenChange(false)} onConfirm={() => c.run(false)} />}
      </DialogContent>
    </Dialog>
  );
}
